import { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Search, Filter, Download, Loader2 } from 'lucide-react';
import { Header, CrewCard, CrewDetailModal } from '../components';
import { useActiveCrew } from '../hooks/useSearch';
import type { CrewMember } from '../types';

export default function ActiveCrewPage() {
  const { data: activeCrew, isLoading, error } = useActiveCrew();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (crew: CrewMember) => {
    setSelectedCrew(crew);
    setIsModalOpen(true);
  };

  const filteredCrew = activeCrew?.filter(crew => 
    crew.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    crew.cid.toLowerCase().includes(searchTerm.toLowerCase()) ||
    crew.rank.toLowerCase().includes(searchTerm.toLowerCase()) ||
    crew.vessel?.toLowerCase().includes(searchTerm.toLowerCase())
  ) ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Active Crew</h1>
              <p className="text-gray-500">View and manage currently active crew members</p>
            </div>
          </div>
        </motion.div>

        {/* Search and Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-6"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, ID, rank, or vessel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter
              </button>
              <button className="btn-secondary flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </motion.div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            {isLoading ? (
              'Loading...'
            ) : (
              <>Showing {filteredCrew.length} active crew members</>
            )}
          </p>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        ) : error ? (
          <div className="card text-center py-12">
            <p className="text-red-600">Failed to load active crew. Please try again.</p>
          </div>
        ) : filteredCrew.length === 0 ? (
          <div className="card text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No crew members found matching your search.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCrew.map((crew, index) => (
              <CrewCard
                key={crew.cid}
                crew={crew}
                index={index}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}
      </main>

      <CrewDetailModal
        crew={selectedCrew}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
